import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../stores';
import { logout, persistor } from '../stores';
import { clearAuthCookies, hasLoggedInCookie, persistAuthTokens } from '../utils/authUtils';
import { clearAnalysisDraft } from '../utils/analysisDraftStorage';

/**
 * Keeps Redux auth in sync with shared SSO cookies.
 * When famla-automate clears cookies on logout, this tab logs out too.
 */
export function useSharedAuthSync(): void {
    const dispatch = useAppDispatch();
    const loggedIn = useAppSelector((state) => state.auth.logged_in);
    const accessToken = useAppSelector((state) => state.auth.access_token);
    const [bootstrapped, setBootstrapped] = useState(
        () => persistor.getState().bootstrapped
    );

    useEffect(() => {
        if (bootstrapped) return;
        return persistor.subscribe(() => {
            if (persistor.getState().bootstrapped) {
                setBootstrapped(true);
            }
        });
    }, [bootstrapped]);

    useEffect(() => {
        if (!bootstrapped) return;

        const syncFromCookies = () => {
            if (!loggedIn || !accessToken) return;

            const skipUntil = Number(sessionStorage.getItem('auth_sync_skip_until') || 0);
            if (Date.now() < skipUntil) return;

            if (hasLoggedInCookie()) return;

            // Après login : access_token peut être HttpOnly côté API ou pas encore écrit.
            // Réécrire les cookies depuis Redux avant de forcer la déconnexion.
            persistAuthTokens(accessToken);
            if (hasLoggedInCookie()) return;

            clearAuthCookies();
            clearAnalysisDraft();
            dispatch(logout());
        };

        syncFromCookies();
        window.addEventListener('focus', syncFromCookies);
        document.addEventListener('visibilitychange', syncFromCookies);

        return () => {
            window.removeEventListener('focus', syncFromCookies);
            document.removeEventListener('visibilitychange', syncFromCookies);
        };
    }, [accessToken, bootstrapped, dispatch, loggedIn]);
}

export default useSharedAuthSync;
